import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { Radio, Loader2, AlertTriangle, Link2 } from 'lucide-react';

import { describeModel, SPORT_LABELS, type Sport } from './sport';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  describeClock,
  NO_CLOCK,
  type ArmResponse,
  type LegKey,
  type LegStatus,
  type Side,
  type SidelineApi,
  type Venue,
  type StatusResponse,
  type TriggerOutcome
} from '@/api/sideline';

/**
 * Deliberately below the service default of 200. The sweep has not yet been proven against a live
 * book, and the evidence that it works — an average fill below the limit — is just as visible on 20
 * shares as on 200. Raise it once a real match shows the order crossing more than one level.
 *
 * This is per LEG: a tap on a three-way fixture places up to three orders of this size.
 */
const DEFAULT_SHARES = 20;
/** Matches the service default: the most we pay above the best bid, and the spread gate with it. */
const DEFAULT_PAY_UP_CENTS = 10;
const MAX_PAY_UP_CENTS = 15;
const STATUS_POLL_MS = 2000;
const RECONNECT_MAX_MS = 5000;

/**
 * Heartbeat cadence. An idle WebSocket gets closed by the ingress read timeout during a quiet
 * stretch of the game, which would cost a reconnect on exactly the tap that mattered. Pinging keeps
 * it pinned open; missing pongs mean the socket is half-open and should be torn down now rather
 * than discovered mid-match.
 */
const HEARTBEAT_INTERVAL_MS = 2_000;
const HEARTBEAT_TIMEOUT_MS = 50_000;

/**
 * The connection meter: each heartbeat carries its send time, the server echoes it, and the round
 * trip is shown next to the live badge in green / amber / red. It exists so the operator can pick a
 * seat with the phone before kick-off — the tap's own timing only exists once a tap has been made.
 * Thresholds are phone-to-server round trips; the orders add their own time on top.
 */
const PING_GOOD_MS = 150;
const PING_POOR_MS = 400;
/** No pong for this long shows red — the socket may be open but nothing is coming back. */
const PING_STALE_MS = 6_000;

/**
 * Phone-to-server round trip of the latest heartbeat, coloured so it can be read from a pocket
 * glance: green under `PING_GOOD_MS`, amber under `PING_POOR_MS`, red above that or when no pong
 * has come back for `PING_STALE_MS`. Updates every heartbeat, so moving seats shows within seconds.
 */
function ConnectionMeter({ ping, now }: { ping: { ms: number; at: number } | null; now: number }) {
  const stale = !ping || now - ping.at > PING_STALE_MS;
  const tone = stale ? 'red' : ping.ms < PING_GOOD_MS ? 'green' : ping.ms < PING_POOR_MS ? 'amber' : 'red';
  const dot = tone === 'green' ? 'bg-emerald-500' : tone === 'amber' ? 'bg-amber-500' : 'bg-red-500';
  const text = tone === 'green' ? 'text-emerald-500' : tone === 'amber' ? 'text-amber-500' : 'text-red-500';
  return (
    <span className={`flex items-center gap-1.5 font-mono text-sm ${text}`} title="phone ↔ server round trip">
      <span className={`inline-block size-3 rounded-full ${dot}`} />
      {ping && !stale ? `${ping.ms} ms` : ping ? 'no reply' : 'measuring'}
    </span>
  );
}

interface LogLine {
  id: number;
  text: string;
  tone: 'ok' | 'warn' | 'err' | 'info';
}

/**
 * Hardware keys that report a goal, so the page can be driven without looking at it.
 *
 * Two things reach this: a Bluetooth clicker or keyboard paired to the phone (which iOS delivers to
 * Safari as ordinary keydown events), and the `ios/sideline-remote` wrapper, which maps the volume
 * buttons onto these. Page Up/Down are what presentation remotes send; the arrows and 1/2 are for
 * a plain keyboard.
 *
 * Volume keys are deliberately absent — no browser on any platform delivers them to a page, which
 * is the entire reason the native wrapper exists.
 */
const KEY_BINDINGS: Record<string, Side> = {
  PageUp: 'A',
  ArrowUp: 'A',
  ArrowLeft: 'A',
  '1': 'A',
  PageDown: 'B',
  ArrowDown: 'B',
  ArrowRight: 'B',
  '2': 'B'
};

interface NativeBridge {
  postMessage: (body: unknown) => void;
}

/**
 * True inside the `ios/sideline-remote` wrapper.
 *
 * Detected off the message handler the wrapper registers rather than a flag it injects, so this
 * needs no change to the app and no reinstall on the phone — a handler named `sideline` exists only
 * because `SidelineWebView` added it. `__sidelineNative` is honoured if a future build sets one.
 */
function isNativeWrapper(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const w = window as unknown as {
    __sidelineNative?: boolean;
    webkit?: { messageHandlers?: Record<string, NativeBridge> };
  };
  return Boolean(w.__sidelineNative ?? w.webkit?.messageHandlers?.sideline);
}

/**
 * Reports an outcome to the native wrapper so it can buzz differently for a fill than for a skip —
 * the whole point of the wrapper is that you never look at the screen, so the phone has to tell you
 * what happened. A no-op in an ordinary browser, where `webkit.messageHandlers` does not exist.
 */
function notifyNative(kind: 'fill' | 'skip'): void {
  const handlers = (window as unknown as { webkit?: { messageHandlers?: Record<string, NativeBridge> } }).webkit
    ?.messageHandlers;
  try {
    handlers?.sideline?.postMessage({ kind });
  } catch {
    // The bridge is a convenience; never let it break a trigger.
  }
}

/**
 * `18:52:37.412`. Milliseconds and a 24-hour clock, because this log exists to be read against
 * order timestamps and price history — and a tap, its ack and its fill routinely land inside the
 * same second, where `5:52:38 PM` on three consecutive lines says nothing at all.
 */
function stamp(at: Date = new Date()): string {
  return `${at.toLocaleTimeString([], { hour12: false })}.${String(at.getMilliseconds()).padStart(3, '0')}`;
}

function fmt(price: number | null | undefined): string {
  return typeof price === 'number' ? `${(price * 100).toFixed(0)}¢` : '—';
}

/** One decimal, because the whole point of the sweep is an average that sits between two ticks. */
function fmtAvg(price: number | null | undefined): string {
  return typeof price === 'number' ? `${(price * 100).toFixed(1)}¢` : '—';
}

/** `187/200 @ 37.4¢ (limit 44¢)` — limit against actual, which is how a sweep proves it swept. */
function describeFill(fill: { price?: number; shares?: number; filledShares?: number; avgPrice?: number | null }) {
  const size = `${fill.filledShares ?? fill.shares ?? 0}/${fill.shares ?? 0}`;
  return `${size} @ ${fmtAvg(fill.avgPrice)} (limit ${fmt(fill.price)})`;
}

/** The live book and position for one token on one venue, looked up by its leg key. */
function legStatus(status: StatusResponse | undefined, key: LegKey, venue: Venue): LegStatus | undefined {
  const [market, outcome] = key.split(':') as [keyof NonNullable<StatusResponse['markets']>, 'yes' | 'no'];
  const info = status?.markets?.[market];
  return venue === 'kalshi' ? info?.kalshi?.legs[outcome] : info?.legs?.[outcome];
}

/** `K ` in front of a Kalshi leg, so the two venues read apart in a log line. */
function venueTag(venue: Venue): string {
  return venue === 'kalshi' ? 'K ' : '';
}

/**
 * How long a control that changes the page's safety state must be held before it fires.
 * Long enough that a pocket cannot produce it, short enough not to be a nuisance at pitchside.
 */
const HOLD_MS = 800;

/**
 * A button that fires only after a sustained press.
 *
 * Both controls that use it change whether this page can spend money, and both sit on a screen that
 * spends the match in a pocket — which produces taps in abundance and sustained deliberate holds
 * essentially never. Releasing early cancels, so a mis-press costs nothing.
 */
function HoldButton({
  onHold,
  className,
  holdingLabel,
  children
}: {
  onHold: () => void;
  className?: string;
  holdingLabel: string;
  children: ReactNode;
}) {
  const [holding, setHolding] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setHolding(false);
  }, []);

  useEffect(() => cancel, [cancel]);

  const start = useCallback(() => {
    setHolding(true);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setHolding(false);
      // Confirms the hold landed without needing to look at the screen.
      if (navigator.vibrate) {
        navigator.vibrate([12, 40, 24]);
      }
      onHold();
    }, HOLD_MS);
  }, [onHold]);

  return (
    <button
      // preventDefault on the press: stops the long-press turning into a text selection or an iOS
      // callout, which is half of what this screen is trying to get rid of.
      onPointerDown={event => {
        event.preventDefault();
        start();
      }}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      onContextMenu={event => event.preventDefault()}
      className={`select-none transition-colors ${holding ? 'bg-amber-500/25' : ''} ${className ?? ''}`}
    >
      {holding ? holdingLabel : children}
    </button>
  );
}

/**
 * The arm form and the two tap buttons, for one sport. `api` is the service the page drives
 * (`sideline-trader` or `courtside-trader`); `sport` chooses the few words that differ.
 */
export function SidelinePage({ api, sport }: { api: SidelineApi; sport: Sport }) {
  const labels = SPORT_LABELS[sport];
  const [marketUrl, setMarketUrl] = useState('');
  const [team, setTeam] = useState('');
  const [shares, setShares] = useState(DEFAULT_SHARES);
  const [payUp, setPayUp] = useState(DEFAULT_PAY_UP_CENTS);
  /** Optional. The same fixture on Kalshi — event ticker, market ticker or kalshi.com URL. */
  const [kalshiEvent, setKalshiEvent] = useState('');
  /**
   * Paper mode. Everything runs — clock, model, every market, both venues — but a tap simulates its
   * fills against the live book and writes them to the DB as simulated orders. Nothing is posted.
   */
  const [paper, setPaper] = useState(false);
  /**
   * Real orders outside the match. Off by default so a tap before kick-off or after full time — a
   * phone in a pocket — is refused by the clock rather than placed.
   */
  const [allowOutsideMatch, setAllowOutsideMatch] = useState(false);
  /**
   * Auto-sell, on every venue the game trades on: what a tap buys is offered straight back — a
   * resting sell at the model's value less a cent, re-posted on every score change, and a sale at
   * the bid once the spread has closed and this many seconds have passed since the score changed.
   */
  const [autoSell, setAutoSell] = useState(true);
  const [autoSellWait, setAutoSellWait] = useState(60);
  const [arming, setArming] = useState(false);
  const [armError, setArmError] = useState<string | null>(null);
  const [armed, setArmed] = useState<ArmResponse | null>(null);
  const [socketLive, setSocketLive] = useState(false);
  /** Round trip of the latest heartbeat, and when it came back — null until the first pong. */
  const [ping, setPing] = useState<{ ms: number; at: number } | null>(null);
  /** The same reading for the tap path, which must not re-create `fire` on every heartbeat. */
  const pingRef = useRef<{ ms: number; at: number } | null>(null);
  const [now, setNow] = useState(() => Date.now());
  /**
   * Inside the native wrapper the volume buttons are the trigger, so the on-screen panels stop
   * being buttons: a phone that lives in a pocket for 90 minutes will otherwise buy a team with a
   * thigh. Left on in an ordinary browser, where touch is the only input there is.
   */
  const [touchArmed, setTouchArmed] = useState(!isNativeWrapper());
  /**
   * Hard touch lock, native wrapper only. In a stand the phone picked up a stray disarm and kept
   * selecting text, so this kills every tap, drag and long-press on the page while leaving scrolling
   * alone. There is deliberately no way out from inside: closing and reopening the app clears it,
   * because a visible unlock control is one more thing a pocket can press.
   */
  const [screenLocked, setScreenLocked] = useState(false);
  const [log, setLog] = useState<LogLine[]>([]);
  const [busy, setBusy] = useState(false);

  const wsRef = useRef<WebSocket | null>(null);
  const seqRef = useRef<number>(Date.now() % 1_000_000);
  /**
   * When each tap left the phone, keyed by seq. Every timing below is a difference of two readings
   * from THIS clock, never a comparison against the server's — the two are minutes apart on a phone
   * that has been in a pocket, and a skew of even a second would swamp what we are measuring.
   */
  const tapAtRef = useRef<Map<number, number>>(new Map());
  const logIdRef = useRef(0);
  const closingRef = useRef(false);

  const {
    data: status,
    isLoading: statusLoading,
    error: statusError
  } = useQuery<StatusResponse>({
    queryKey: ['sideline-status', api.service],
    queryFn: () => api.status(),
    refetchInterval: STATUS_POLL_MS,
    retry: false
  });

  const addLog = useCallback((text: string, tone: LogLine['tone'] = 'info') => {
    logIdRef.current += 1;
    const line = { id: logIdRef.current, text: `${stamp()}  ${text}`, tone };
    setLog(prev => [line, ...prev].slice(0, 80));
  }, []);

  /**
   * Writes a tap's result to the log, one line per leg. A tap on a three-way fixture is up to three
   * orders and each fills or skips on its own, so a single "placed"/"skipped" line would hide which
   * of them did what.
   */
  const logOutcome = useCallback(
    (outcome: TriggerOutcome, timing: string) => {
      if (!outcome.legs.length) {
        addLog(`${outcome.side} skip: ${outcome.reason}${timing}`, 'warn');
        notifyNative('skip');
        return;
      }
      for (const leg of outcome.legs) {
        const name = `${venueTag(leg.venue)}${leg.label}`;
        if (leg.fired) {
          addLog(
            `${outcome.side} ${name} ${leg.simulated ? 'PAPER FILL' : 'ORDER PLACED'} ${describeFill(leg)}  ${leg.latencyMs}ms`,
            'ok'
          );
        } else {
          addLog(`${outcome.side} ${name} skip: ${leg.reason}  ${leg.latencyMs}ms`, 'warn');
        }
      }
      const score = outcome.score ? `score ${outcome.score.A}–${outcome.score.B}` : '';
      addLog(`${outcome.side} done — ${score}${timing}`, outcome.fired ? 'ok' : 'warn');
      notifyNative(outcome.fired ? 'fill' : 'skip');
    },
    [addLog]
  );

  // Adopt a game that is already armed (page refresh, or armed from curl) so the buttons work
  // without re-arming and blowing away the ladders.
  useEffect(() => {
    if (!armed && status?.armed && status.teams && status.markets && status.taps) {
      const markets = {} as ArmResponse['markets'];
      for (const market of Object.values(status.markets)) {
        markets[market.key] = {
          ...market,
          legs: market.legs
            ? {
                yes: { label: market.legs.yes.label, tokenId: '', rungs: 0 },
                no: { label: market.legs.no.label, tokenId: '', rungs: 0 }
              }
            : null,
          kalshi: market.kalshi
            ? {
                ...market.kalshi,
                legs: {
                  yes: { label: market.kalshi.legs.yes.label, tokenId: null, rungs: 0 },
                  no: { label: market.kalshi.legs.no.label, tokenId: null, rungs: 0 }
                }
              }
            : null
        };
      }
      setArmed({
        armed: true,
        eventSlug: status.eventSlug ?? '',
        resolvedFrom: status.resolvedFrom ?? 'market-slug',
        teams: status.teams,
        moneyline: status.moneyline ?? { A: 'A', B: 'B', draw: 'draw' },
        ignored: status.ignored ?? [],
        sharesPerTrigger: status.sharesPerTrigger ?? DEFAULT_SHARES,
        payUpBudget: status.payUpBudget ?? DEFAULT_PAY_UP_CENTS / 100,
        simulated: status.simulated ?? false,
        model: status.model ?? null,
        clock: status.clock ?? NO_CLOCK,
        score: status.score ?? { A: 0, B: 0 },
        firstScorer: status.firstScorer ?? null,
        kalshi: status.kalshi ?? null,
        markets,
        taps: status.taps
      });
    }
    if (armed && status && !status.armed) {
      setArmed(null);
    }
  }, [status, armed]);

  // Hold the trigger socket open for the whole game. A tap then costs one network trip instead of
  // a fresh TCP + TLS handshake, which on arena cellular is the largest chunk of the whole path.
  // The socket authenticates with the backoffice session JWT in its first frame, not in the URL.
  useEffect(() => {
    if (!armed) {
      return;
    }
    const tick = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(tick);
  }, [armed]);

  useEffect(() => {
    if (!armed) {
      return;
    }

    closingRef.current = false;
    let backoff = 250;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let heartbeat: ReturnType<typeof setInterval> | null = null;
    let lastPongAt = 0;

    const stopHeartbeat = () => {
      if (heartbeat) {
        clearInterval(heartbeat);
        heartbeat = null;
      }
    };

    const startHeartbeat = (ws: WebSocket) => {
      stopHeartbeat();
      lastPongAt = Date.now();
      heartbeat = setInterval(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          return;
        }
        if (Date.now() - lastPongAt > HEARTBEAT_TIMEOUT_MS) {
          // Half-open: the socket looks fine but nothing is coming back. Close it so onclose
          // schedules a reconnect, rather than finding out on the next tap.
          addLog('heartbeat lost, reconnecting', 'warn');
          ws.close();
          return;
        }
        ws.send(JSON.stringify({ type: 'ping', t: Date.now() }));
      }, HEARTBEAT_INTERVAL_MS);
    };

    const connect = () => {
      if (closingRef.current) {
        return;
      }
      const ws = new WebSocket(api.triggerSocketUrl());
      wsRef.current = ws;

      ws.onopen = () => {
        backoff = 250;
        ws.send(api.authFrame());
      };
      ws.onclose = () => {
        stopHeartbeat();
        setSocketLive(false);
        setPing(null);
        if (!closingRef.current) {
          retryTimer = setTimeout(connect, backoff);
          backoff = Math.min(backoff * 2, RECONNECT_MAX_MS);
        }
      };
      ws.onmessage = event => {
        const message = JSON.parse(event.data) as TriggerOutcome & { type?: string; ok?: boolean; error?: string };

        // The socket is only usable once the server has accepted the auth frame.
        if (message.type === 'auth') {
          if (message.ok) {
            setSocketLive(true);
            addLog('trigger socket authenticated', 'ok');
            startHeartbeat(ws);
          } else {
            addLog(`socket auth rejected: ${message.error ?? 'unknown'}`, 'err');
          }
          return;
        }

        if (message.type === 'pong') {
          lastPongAt = Date.now();
          const sentAt = (message as { t?: unknown }).t;
          if (typeof sentAt === 'number') {
            pingRef.current = { ms: Math.max(0, lastPongAt - sentAt), at: lastPongAt };
            setPing(pingRef.current);
          }
          return;
        }

        // Stage 2 of 3. The server acks the moment the frame lands, before it starts the orders, so
        // this is a ROUND TRIP of a near-empty frame: up, a few microseconds of handler, and back.
        // Not the one-way trip up — nothing here can measure that without trusting two clocks
        // against each other. What it does isolate is the network cost of a trivial exchange, which
        // is what the orders' own timing has to be read against.
        if (message.type === 'ack') {
          const tapAt = typeof message.seq === 'number' ? tapAtRef.current.get(message.seq) : undefined;
          addLog(`${message.side ?? '?'} ack${tapAt ? `  +${Date.now() - tapAt}ms round trip` : ''}`, 'info');
          return;
        }

        // Stage 3 of 3. `latencyMs` is the server's own measurement of its own work — frame in to
        // the slowest CLOB POST returned — so the remainder is everything else: both network legs
        // plus whatever the phone spent scheduling. Splitting it out is the point: if the order is
        // slow it is worth knowing whether that is Polymarket or the stadium, and those want
        // opposite fixes.
        const tapAt = typeof message.seq === 'number' ? tapAtRef.current.get(message.seq) : undefined;
        const roundTrip = tapAt ? Date.now() - tapAt : undefined;
        const timing = roundTrip
          ? `  +${roundTrip}ms (${message.latencyMs}ms server · ${roundTrip - (message.latencyMs ?? 0)}ms network)`
          : `  ${message.latencyMs}ms`;
        if (typeof message.seq === 'number') {
          tapAtRef.current.delete(message.seq);
        }

        logOutcome({ ...message, legs: message.legs ?? [] }, timing);
      };
    };

    connect();

    return () => {
      closingRef.current = true;
      stopHeartbeat();
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
      wsRef.current?.close();
      wsRef.current = null;
      setSocketLive(false);
    };
  }, [armed, api, addLog, logOutcome]);

  const handleArm = async () => {
    // A Polymarket match, or a Kalshi event on its own for a match Polymarket does not list.
    if (!marketUrl.trim() && !kalshiEvent.trim()) {
      return;
    }
    setArming(true);
    setArmError(null);
    try {
      const result = await api.arm({
        marketUrl: marketUrl.trim() || undefined,
        team: team.trim() || undefined,
        sharesPerTrigger: shares,
        payUpBudget: payUp / 100,
        kalshiEvent: kalshiEvent.trim() || undefined,
        simulated: paper,
        allowOutsideMatch,
        // Nothing is sold in paper mode, so the box means nothing there. The basketball service
        // never sells, so it is not told about auto-sell at all.
        ...(labels.autoSell ? { autoSell: autoSell && !paper, autoSellWaitSeconds: autoSellWait } : {})
      });
      if (result.simulated) {
        addLog('PAPER MODE — fills are simulated against the live book and saved as simulated orders', 'warn');
      }
      setArmed(result);
      const markets = Object.values(result.markets);
      const groups = Array.from(new Set(markets.map(m => m.group)));
      addLog(`armed ${result.teams.A} vs ${result.teams.B} — ${markets.length} markets: ${groups.join(' · ')}`, 'ok');
      if (result.ignored.length) {
        addLog(`left out: ${result.ignored.slice(0, 6).join('; ')}${result.ignored.length > 6 ? '…' : ''}`, 'warn');
      }
      addLog(
        `clock: ${describeClock(result.clock)} · score ${result.score.A}–${result.score.B}${
          result.model ? ` · ${describeModel(result.model)}` : ' · no model, flat pay-up'
        }`,
        result.model ? 'info' : 'warn'
      );
      if (result.kalshi) {
        addLog(
          result.resolvedFrom === 'kalshi-event'
            ? `Kalshi only: ${result.kalshi.eventTicker}, ${result.kalshi.contractsPerTrigger} contracts/leg — Polymarket does not list this match`
            : `Kalshi armed too: ${result.kalshi.eventTicker}, ${result.kalshi.contractsPerTrigger} contracts/leg, markets paired by ${result.kalshi.matchedBy}`,
          result.kalshi.matchedBy === 'names' ? 'ok' : 'warn'
        );
      }
      for (const side of ['A', 'B'] as const) {
        addLog(
          `tap ${result.teams[side]} buys ${result.taps[side].map(l => `${venueTag(l.venue)}${l.label}`).join(', ')}`,
          'info'
        );
      }
    } catch (error) {
      setArmError(error instanceof Error ? error.message : String(error));
    } finally {
      setArming(false);
    }
  };

  // pointerdown, not click: fires on touch-down rather than touch-up, worth ~80-120ms.
  const fire = useCallback(
    (side: Side) => {
      const ws = wsRef.current;
      seqRef.current += 1;
      const seq = seqRef.current;

      // Stage 1 of 3, written before anything else happens. If a tap is ever lost this is the line
      // that proves the phone saw it, which is the difference between a dead button and a dead
      // network.
      const tapAt = Date.now();
      tapAtRef.current.set(seq, tapAt);
      addLog(`${side} tapped`, 'info');

      if (socketLive && ws && ws.readyState === WebSocket.OPEN) {
        // The latest heartbeat's round trip rides along, so the review can show the tap's trip up —
        // the one leg the server cannot time. Left out when stale: an old reading is not this tap's.
        const latest = pingRef.current;
        const rtt = latest && Date.now() - latest.at < PING_STALE_MS ? latest.ms : undefined;
        ws.send(JSON.stringify({ side, seq, rtt }));
        if (navigator.vibrate) {
          navigator.vibrate(15);
        }
        return;
      }

      addLog('socket down — falling back to HTTP', 'warn');
      api
        .trigger(side, seq)
        .then(outcome => {
          // No ack on this path — a fresh HTTP request has no earlier moment to report, and the
          // network figure here also carries a TCP and TLS handshake the socket had already paid.
          const total = Date.now() - tapAt;
          const timing = `  +${total}ms (${outcome.latencyMs}ms server · ${total - outcome.latencyMs}ms network+handshake)`;
          tapAtRef.current.delete(seq);
          logOutcome(outcome, timing);
        })
        .catch(error => addLog(`trigger failed: ${error}`, 'err'));
    },
    [socketLive, api, addLog, logOutcome]
  );

  // Hardware triggers, so a tap costs neither a glance at the screen nor a hand out of a pocket.
  // Published ONLY while armed: before kickoff a stray key press must not be able to spend money,
  // and after disarming there is no game to fire at.
  useEffect(() => {
    if (!armed) {
      return;
    }

    const bridge = window as unknown as {
      __sidelineFire?: (side: Side) => void;
    };
    // The native wrapper calls this rather than synthesising key events, which WKWebView cannot do.
    bridge.__sidelineFire = fire;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || event.repeat) {
        return;
      }
      const target = event.target as HTMLElement | null;
      // Never steal a key from a field the operator is typing into.
      if (target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))) {
        return;
      }
      const side = KEY_BINDINGS[event.key];
      if (!side) {
        return;
      }
      event.preventDefault();
      fire(side);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      delete bridge.__sidelineFire;
    };
  }, [armed, fire]);

  /**
   * The touch lock. Swallows every pointer interaction on the page while leaving scrolling intact,
   * so the activity log can still be read from a pocket without a stray press disarming the game or
   * dragging out a text selection — both of which happened in the first live match.
   *
   * Capture phase on `document`: React attaches its own listeners at the root, so stopping
   * propagation here means no component handler ever runs, including the Disarm button and the
   * sidebar links.
   *
   * `touchstart`, `touchmove`, `wheel` and `scroll` are deliberately NOT touched — cancelling those
   * is what would break scrolling. `pointerdown` is stopped but not defaultPrevented for the same
   * reason: preventing its default cancels the scroll gesture that follows.
   *
   * Volume keys are unaffected. They arrive through `window.__sidelineFire`, not as DOM events.
   */
  useEffect(() => {
    if (!screenLocked) {
      return;
    }

    const swallow = (event: Event) => {
      event.stopPropagation();
      // Only where the default action is itself the damage: navigation, selection, the callout menu.
      if (['click', 'dblclick', 'contextmenu', 'selectstart', 'dragstart'].includes(event.type)) {
        event.preventDefault();
      }
    };

    const events = [
      'click',
      'dblclick',
      'contextmenu',
      'selectstart',
      'dragstart',
      'pointerdown',
      'pointerup',
      'mousedown',
      'mouseup'
    ];
    for (const type of events) {
      document.addEventListener(type, swallow, true);
    }

    // setProperty rather than the camelCase fields: `-webkit-touch-callout` is the one that stops
    // the long-press menu on iOS and it is not in the DOM typings.
    const style = document.body.style;
    const suppressed = ['user-select', '-webkit-user-select', '-webkit-touch-callout'];
    const previous = suppressed.map(name => style.getPropertyValue(name));
    for (const name of suppressed) {
      style.setProperty(name, 'none');
    }

    return () => {
      for (const type of events) {
        document.removeEventListener(type, swallow, true);
      }
      suppressed.forEach((name, i) => {
        if (previous[i]) {
          style.setProperty(name, previous[i]);
        } else {
          style.removeProperty(name);
        }
      });
    };
  }, [screenLocked]);

  const handleDisarm = async () => {
    setBusy(true);
    try {
      await api.disarm();
      setArmed(null);
      addLog('disarmed', 'info');
    } catch (error) {
      addLog(`disarm failed: ${error}`, 'err');
    } finally {
      setBusy(false);
    }
  };

  if (statusLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Contacting {labels.service}…
      </div>
    );
  }

  if (statusError) {
    return (
      <Card className="border-destructive">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="size-5" /> Cannot reach {labels.service}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>{statusError instanceof Error ? statusError.message : String(statusError)}</p>
          <p>
            Tried <code>{api.baseUrl()}</code>. The service authenticates with your backoffice session, so a 401 here
            means your session expired — log out and back in.
          </p>
        </CardContent>
      </Card>
    );
  }

  // The legs each tap buys right now. The draw side depends on the score, so this is read off the
  // live status rather than the arm response — after a goal the same button buys the other side of
  // the Draw market.
  const taps = status?.taps ?? armed?.taps;
  const armedMarkets = armed ? Object.values(armed.markets) : [];
  const moneylineMarkets = armed
    ? [armed.moneyline.A, armed.moneyline.draw, armed.moneyline.B]
        .filter((key): key is string => key !== null)
        .map(key => armed.markets[key])
        .filter((m): m is NonNullable<typeof m> => Boolean(m))
    : [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold">{labels.name}</h1>
        {armed ? (
          <Badge variant={socketLive ? 'default' : 'destructive'} className="gap-1">
            <Radio className="size-3" />
            {socketLive ? 'live' : 'socket down'}
          </Badge>
        ) : null}
        {armed && socketLive ? <ConnectionMeter ping={ping} now={now} /> : null}
        {status?.armed && !status.bookConnected ? <Badge variant="destructive">book disconnected</Badge> : null}
        {armed?.simulated ? <Badge variant="outline">PAPER</Badge> : null}
        <span className={`ml-auto text-xs ${armed?.simulated ? 'text-amber-500' : 'text-muted-foreground'}`}>
          {armed?.simulated
            ? 'Paper mode. Nothing is posted — taps simulate fills against the live book and save them as simulated orders.'
            : 'Real money. No exposure cap. Taps place live orders.'}
        </span>
      </div>

      {!armed ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Arm a {labels.match}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-col gap-3 sm:flex-row">
              <Input
                placeholder={labels.urlPlaceholder}
                value={marketUrl}
                onChange={e => setMarketUrl(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleArm()}
                className="flex-1"
              />
              <Input
                placeholder="team on button A (optional)"
                value={team}
                onChange={e => setTeam(e.target.value)}
                className="sm:w-52"
                aria-label="Team to put on button A"
              />
              <Input
                type="number"
                min={1}
                value={shares}
                onChange={e => setShares(Number(e.target.value))}
                className="sm:w-24"
                aria-label="Shares per leg"
              />
              <Input
                type="number"
                min={1}
                max={MAX_PAY_UP_CENTS}
                value={payUp}
                onChange={e => setPayUp(Number(e.target.value))}
                className="sm:w-24"
                aria-label="Pay up to, in cents above the bid"
              />
              <Input
                placeholder="Kalshi event (optional, or alone)"
                value={kalshiEvent}
                onChange={e => setKalshiEvent(e.target.value)}
                className="sm:w-64"
                aria-label="Kalshi event ticker or URL"
              />
              <label className="flex items-center gap-2 whitespace-nowrap text-sm">
                <Checkbox
                  checked={paper}
                  onCheckedChange={value => setPaper(value === true)}
                  aria-label="Paper trade"
                />
                paper
              </label>
              <label className="flex items-center gap-2 whitespace-nowrap text-sm">
                <Checkbox
                  checked={allowOutsideMatch}
                  onCheckedChange={value => setAllowOutsideMatch(value === true)}
                  aria-label="Allow real orders outside the match"
                />
                allow outside {labels.match}
              </label>
              {labels.autoSell ? (
                <label
                  className={`flex items-center gap-2 whitespace-nowrap text-sm ${paper ? 'opacity-50' : ''}`}
                  title="Sells what the taps buy, on Polymarket and on Kalshi. Does nothing in paper mode."
                >
                  <Checkbox
                    checked={autoSell}
                    onCheckedChange={value => setAutoSell(value === true)}
                    aria-label="Auto-sell"
                  />
                  auto-sell
                  <Input
                    type="number"
                    min={0}
                    max={900}
                    value={autoSellWait}
                    onChange={e => setAutoSellWait(Math.max(0, Math.min(900, Number(e.target.value) || 0)))}
                    className="h-8 w-20"
                    aria-label="Seconds to wait after a score change before selling at the bid"
                    disabled={!autoSell}
                  />
                  s
                </label>
              ) : null}
              <Button onClick={handleArm} disabled={arming || (!marketUrl.trim() && !kalshiEvent.trim())}>
                {arming ? <Loader2 className="size-4 animate-spin" /> : null}
                Arm
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Paste the match URL, optionally name the team for button A (defaults to the home side), then shares per
              leg and how many cents above the best bid to pay. A match Polymarket does not list can be armed off its
              Kalshi event alone: leave the URL empty and paste the Kalshi link. Each button is a team: tap whoever
              scored and the service buys every market on the fixture that goal moves — the moneyline (the scorer&apos;s
              Yes, the other team&apos;s No, the Draw toward or away from level), totals, team totals, spreads, first to
              score, both teams to score and the halftime result — each an order of the size given, on the side the goal
              lifts. Nothing is ever bought above 90¢: that is the budget for a goal being chalked off. The match clock
              and score are read off the Polymarket event itself, so every limit is priced off what that goal is worth
              to that leg at that minute — including when you arm mid-match; the cents figure is only the fallback for
              when the clock is unavailable, and then only the moneyline trades. Arming pre-signs a ladder per token —
              it takes a while on a full fixture. Paste the same fixture&apos;s Kalshi event (ticker or kalshi.com URL)
              to fire the moneyline legs there too, at the same size, gated and priced against Kalshi&apos;s own book.
              Tick <strong>paper</strong> to rehearse: everything runs the same but a tap simulates its fills against
              the live book and saves them as simulated orders — nothing is posted, no ladders are signed, and no venue
              credentials are needed. Real orders fire only while the clock says the match is in play; tick{' '}
              <strong>allow outside match</strong> to fire them before kick-off or after full time, for a test.
              {labels.autoSell ? (
                <>
                  {' '}
                  <strong>Auto-sell</strong> (both venues, the same rules on each) offers everything a tap buys straight
                  back: a resting sell at the model&apos;s value of the outcome less 1¢, with no time limit, cancelled
                  and re-posted at the new value whenever the score changes; and once the number of seconds given has
                  passed since the score last changed, a position whose market has closed to a spread of 3¢ or less is
                  sold at the bid — a real goal at the re-priced bid, a goal that never stood at whatever the market
                  says. A wide spread is never sold into.
                </>
              ) : null}
            </p>
            {armError ? <p className="text-sm text-destructive">{armError}</p> : null}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="flex flex-wrap items-baseline gap-2 text-sm text-muted-foreground">
            <Link2 className="size-3" />
            <span>
              {armed.teams.A} vs {armed.teams.B}
            </span>
            <span>·</span>
            <span>
              {moneylineMarkets.map(m => m.label).join(' / ')}
              {armedMarkets.length > moneylineMarkets.length
                ? ` + ${armedMarkets.length - moneylineMarkets.length} more markets`
                : ''}
            </span>
            <span>·</span>
            <span>{armed.sharesPerTrigger} shares/leg</span>
            {armed.kalshi ? (
              <>
                <span>·</span>
                <span className={status?.kalshi && !status.kalshi.bookConnected ? 'text-amber-500' : ''}>
                  Kalshi {armed.kalshi.eventTicker} · {armed.kalshi.markets} markets ·{' '}
                  {armed.kalshi.contractsPerTrigger}/leg
                  {status?.kalshi && !status.kalshi.bookConnected ? ' · book stale' : ''}
                </span>
              </>
            ) : null}
            <span>·</span>
            {armed.model ? (
              <span>
                {describeModel(status?.model ?? armed.model)}
                {status?.modelFittedAt
                  ? ` · fitted ${new Date(status.modelFittedAt).toLocaleTimeString([], { hour12: false })} from the ${status.modelSource ?? 'book'}`
                  : ''}
              </span>
            ) : (
              <span>pay up to bid +{Math.round((armed.payUpBudget ?? 0) * 100)}¢</span>
            )}
            <span>·</span>
            {status?.clock?.source ? (
              <>
                <span>·</span>
                <span className="text-muted-foreground">feed {status.clock.source}</span>
              </>
            ) : null}
            <span className={status?.clock?.stale || status?.clock?.phase === 'no-clock' ? 'text-amber-500' : ''}>
              {describeClock(status?.clock ?? armed.clock, labels.clock)}
            </span>
            {status?.score ? (
              <>
                <span>·</span>
                <span>
                  score {status.score.A}–{status.score.B}
                  {status.clock?.feedScore &&
                  (status.clock.feedScore.A !== status.score.A || status.clock.feedScore.B !== status.score.B)
                    ? ` (feed ${status.clock.feedScore.A}–${status.clock.feedScore.B})`
                    : ''}
                </span>
              </>
            ) : null}
            {status?.latencyMs ? (
              <>
                <span>·</span>
                <span>
                  latency {status.latencyMs.min}/{status.latencyMs.median}/{status.latencyMs.max} ms
                </span>
              </>
            ) : null}
          </div>

          {status?.armed && !status.simulated && status.clock?.phase === 'no-clock' ? (
            <p className="rounded-md border border-red-600 bg-red-950/40 px-3 py-2 text-sm text-red-400">
              <strong>No live feed for this fixture.</strong> The score is your taps only and the model has no minute.
              Real orders are refused until a feed picks the match up
              {status.clock?.allowOutsideMatch ? ' — except that allow outside match is on, so they fire anyway' : ''}.
            </p>
          ) : null}
          {screenLocked ? (
            <p className="rounded-md border border-amber-600 px-3 py-2 text-xs text-amber-500">
              <strong>Screen locked.</strong> Every tap, drag and long-press on this page is ignored — only the volume
              keys trade, and only scrolling still works. Close and reopen the app to unlock.
            </p>
          ) : null}

          <p className="text-xs text-muted-foreground">
            Keys fire too, so you never have to look at the screen: <kbd>↑</kbd> <kbd>PgUp</kbd> <kbd>1</kbd> means{' '}
            {armed.teams.A} scored, <kbd>↓</kbd> <kbd>PgDn</kbd> <kbd>2</kbd> means {armed.teams.B} scored. Pair a
            Bluetooth clicker to the phone and it works from your pocket.
          </p>

          <div className="grid gap-3 sm:grid-cols-2">
            {(
              [
                ['A', 'bg-blue-700 hover:bg-blue-600'],
                ['B', 'bg-red-700 hover:bg-red-600']
              ] as const
            ).map(([side, color]) => (
              <button
                key={side}
                // Not `disabled`: the panel is still the live price display, it just stops being a
                // trigger. pointer-events-none is what makes a pocket incapable of placing an order.
                {...(touchArmed
                  ? {
                      onPointerDown: (event: ReactPointerEvent) => {
                        event.preventDefault();
                        fire(side);
                      }
                    }
                  : { tabIndex: -1, 'aria-disabled': true })}
                className={`${color} flex min-h-44 touch-manipulation select-none flex-col items-center justify-center rounded-2xl px-4 py-4 text-white transition ${
                  touchArmed ? 'hover:brightness-110 active:brightness-150' : 'pointer-events-none opacity-90'
                }`}
              >
                <span className="text-3xl font-bold leading-tight">{armed.teams[side]}</span>
                <span className="mt-1 text-xs uppercase tracking-wide opacity-60">scored — buys</span>
                <span className="mt-2 grid grid-cols-[auto_auto_auto] gap-x-3 gap-y-0.5 text-xs sm:text-sm">
                  {(taps?.[side] ?? []).map(tapLeg => {
                    const leg = legStatus(status, tapLeg.leg, tapLeg.venue);
                    return (
                      <span key={`${tapLeg.venue}:${tapLeg.leg}`} className="contents">
                        <span className="text-left font-semibold">
                          {tapLeg.venue === 'kalshi' ? (
                            <span className="mr-1 rounded bg-white/20 px-1 text-[10px] font-bold uppercase">K</span>
                          ) : null}
                          {tapLeg.label}
                        </span>
                        <span className="font-mono opacity-80">
                          {fmt(leg?.top?.bid)} / {fmt(leg?.top?.ask)}
                          {leg?.top && !leg.top.fresh ? ' · stale' : ''}
                        </span>
                        <span className="text-right text-xs opacity-70">{leg?.filledShares ?? 0} held</span>
                      </span>
                    );
                  })}
                </span>
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className={touchArmed ? 'text-muted-foreground' : 'text-amber-500'}>
              {touchArmed ? 'Touch is live — the panels place orders.' : 'Touch disabled — volume keys only.'}
            </span>
            {/* Native wrapper only. In a browser the lock would be a trap: there is no app to close
                and reopen, so the page would be dead until a manual reload. */}
            {isNativeWrapper() && !screenLocked ? (
              <HoldButton
                onHold={() => setScreenLocked(true)}
                holdingLabel="keep holding…"
                className="rounded border border-current px-2 py-0.5 text-amber-500 hover:text-foreground"
              >
                hold to lock screen
              </HoldButton>
            ) : null}
            {/* Deliberately small and out of the way: it must be hard to hit by accident, but it has
                to exist, because a wrapper whose volume capture fails mid-match would otherwise
                leave no way to trade at all. */}
            {/* Held rather than tapped for the same reason as the lock: re-arming touch is what makes
                the buy panels live again, and a pocket must not be able to do that by brushing it. */}
            <HoldButton
              onHold={() => setTouchArmed(prev => !prev)}
              holdingLabel="keep holding…"
              className="rounded border border-current px-2 py-0.5 text-muted-foreground hover:text-foreground"
            >
              {touchArmed ? 'hold to disable touch' : 'hold to enable touch'}
            </HoldButton>
          </div>

          <div className="flex flex-wrap items-center gap-4 text-sm">
            <span>
              fills <strong>{status?.fills ?? 0}</strong>
            </span>
            <span>
              skips <strong>{status?.skips ?? 0}</strong>
            </span>
            <span className={status?.imbalanceShares ? 'text-amber-500' : ''}>
              imbalance <strong>{status?.imbalanceShares ?? 0}</strong>
              {armedMarkets
                .filter(m => (status?.imbalance?.[m.key] ?? 0) > 0)
                .map(m => (
                  <span key={m.key} className="ml-2 text-xs">
                    {m.label} {status?.imbalance?.[m.key]}
                  </span>
                ))}
            </span>
            {armed.kalshi ? (
              <span className={status?.kalshiImbalanceShares ? 'text-amber-500' : ''}>
                Kalshi imbalance <strong>{status?.kalshiImbalanceShares ?? 0}</strong>
                {armedMarkets
                  .filter(m => m.kalshi && (status?.kalshiImbalance?.[m.key] ?? 0) > 0)
                  .map(m => (
                    <span key={m.key} className="ml-2 text-xs">
                      {m.label} {status?.kalshiImbalance?.[m.key]}
                    </span>
                  ))}
              </span>
            ) : null}
            <div className="ml-auto">
              <Button variant="destructive" size="sm" onClick={handleDisarm} disabled={busy}>
                Disarm
              </Button>
            </div>
          </div>

          {labels.autoSell && status?.autoSell?.on ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  Auto-sell{' '}
                  <span className="text-sm font-normal text-muted-foreground">
                    {(status.autoSell.venues ?? []).join(' + ')} · resting sell at value − 1¢ · at the bid once the
                    spread is ≤ 3¢, {status.autoSell.waitSeconds}s after a score change
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {status.autoSell.positions.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nothing bought yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-left text-xs text-muted-foreground">
                        <tr>
                          <th className="py-1 pr-4 font-normal">Market</th>
                          <th className="py-1 pr-4 text-right font-normal">Held</th>
                          <th className="py-1 pr-4 text-right font-normal">Resting sell</th>
                          <th className="py-1 pr-4 text-right font-normal">Sold</th>
                          <th className="py-1 font-normal">Last</th>
                        </tr>
                      </thead>
                      <tbody className="font-mono text-xs">
                        {status.autoSell.positions.map(position => (
                          <tr key={`${position.venue}-${position.instrument}`} className="border-t border-border/50">
                            <td className="py-1 pr-4 font-sans text-sm">
                              {position.venue === 'kalshi' ? (
                                <Badge variant="outline" className="mr-1 px-1 py-0 text-[10px] uppercase">
                                  K
                                </Badge>
                              ) : null}
                              {position.label}
                            </td>
                            <td className="py-1 pr-4 text-right">{position.held}</td>
                            <td className="py-1 pr-4 text-right">
                              {position.resting
                                ? `${position.resting.count} @ ${(position.resting.price * 100).toFixed(0)}¢`
                                : '—'}
                            </td>
                            <td className="py-1 pr-4 text-right">
                              {position.sold > 0 && position.avgSold !== null
                                ? `${position.sold} @ ${(position.avgSold * 100).toFixed(1)}¢`
                                : '—'}
                            </td>
                            <td
                              className={`py-1 font-sans ${position.abandoned ? 'text-destructive' : 'text-muted-foreground'}`}
                            >
                              {position.lastEvent}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : null}

          {status?.imbalanceShares ? (
            <p className="text-xs text-amber-500">
              {status.imbalanceShares} unmatched shares are naked directional exposure — they resolve to $1 or $0 at{' '}
              {labels.fullTime}. A Yes and a No on the same market are a balanced pair, and those are swept back to
              collateral by the liquidator.
            </p>
          ) : null}
        </>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="max-h-72 overflow-y-auto font-mono text-xs leading-relaxed">
            {log.length === 0 ? <span className="text-muted-foreground">No activity yet.</span> : null}
            {log.map(line => (
              <div
                key={line.id}
                className={
                  line.tone === 'ok'
                    ? 'text-green-500'
                    : line.tone === 'warn'
                      ? 'text-amber-500'
                      : line.tone === 'err'
                        ? 'text-destructive'
                        : 'text-muted-foreground'
                }
              >
                {line.text}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
